import type { Unit, Second } from '@/data';
import { Audio } from '@/data';
import type { MetadataFormat } from '@/data/Metadata';
import type { AudioImageFileFormat } from '@/data/sources/Source';
import MetadataParser from '../MetadataParser';

/**
  * An audio file object that:
  * - Contains the relative path to an audio file
  * - Contains a time conversion object
  * - May contain wave image file objects
  */
export interface AudioFileFormat extends MetadataFormat {
  /** The youtube id for youtube integration */
  'yt-id'?: string;
  /** The audio file url */
  file?: string;
  /** The synchronization format, to be parsed in a unit  */
  'onset-date': string;
  /** Images associated with this track */
  images?: AudioImageFileFormat[];
  id?: string;
}

export function generateUniqueId(audioFormat : AudioFileFormat) :string {
  return (audioFormat['yt-id' as keyof AudioFileFormat] ?? audioFormat.file ?? 'audio-') + Math.floor(Math.random() * 100).toString().padStart(2, '0');
}

export function audioFileFormatFromNewFormat(sourceFormat : Map<string, unknown>)
  : AudioFileFormat {
  const refs : Record<string, string> = {};

  (Array.from(sourceFormat.keys()) as string[]).filter((key) => key.startsWith('ref')).forEach((refKey) => { refs[refKey] = sourceFormat.get(refKey) as string; });

  return {
    'yt-id': sourceFormat.get('video:yt') as string ?? sourceFormat.get('audio:yt') as string,
    file: sourceFormat.get('audio') as string ?? sourceFormat.get('audio:x') as string,
    'onset-date': sourceFormat.get('synchro') as string,
    info: sourceFormat.get('text') as string,
    license: sourceFormat.get('license') as string,
    contributors: sourceFormat.get('contributors') as Record<string, string>,
    refs,
    id: sourceFormat.get('id') as string,
    year: sourceFormat.get('year') as string,
  };
}

/**
 * An image file object
 *
 * Contains:
 * * the relative path to an image file
 * * a time conversion object
 *
 * For score images, the conversion should be onset -> pixel
 *
 * For wave images, the conversion should be second -> pixel
 */

export default class AudioParser extends Audio {
  static fromFormat(
    format : AudioFileFormat,
    secondUnit: Unit<Second>,
    url?: string,
    youtubeId?: string,
    id = '',
  ): Audio {
    if (!url && !youtubeId) throw new Error('No source file or id was provided');
    const metadata = MetadataParser.fromFormat(format);
    return new Audio({
      secondUnit,
      url,
      youtubeId,
      metadata,
      id,
    });
  }
}
